import {createApi} from '@reduxjs/toolkit/dist/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {
    AddRfidRequest,
    CreateUserRequest,
    DefaultResponse,
    DeleteRfidRequest,
    LinkingBexioAccountRequest,
    PageBaseUsersInfo,
    PostFeedbackRequest,
    SetPrimaryBexioAccountRequest,
    UpdateManagerRequest,
    UpdateRfidRequest,
    UpdateUserBexioIdRequest,
    UserByIdRequest,
    UserDeleteRequest,
    UserDTO,
    UserImageRequest,
    UserNameUpdateRequest,
    UsersSearchRequest,
    UserUpdateRequest,
} from 'src/types';
import {DailyCounts, UserStats} from 'src/types/stats';
import {getBaseURL} from 'src/utils/helper';

export const usersApi = createApi({
    reducerPath: 'usersApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL() + '/users'),
    }),
    tagTypes: ['User', 'Team'],
    endpoints: (build) => {
        return {
            // The same body the list is fetched with, userTypes included: the list shows four account
            // types unless told otherwise, and the block has to count the same four.
            getUserStats: build.query<UserStats, UsersSearchRequest>({
                query: ({data}) => ({url: '/stats/summary', method: 'post', data}),
                providesTags: [{type: 'User' as const, id: 'LIST'}],
            }),
            getUsersDailyStats: build.query<DailyCounts, UsersSearchRequest>({
                query: ({data}) => ({url: '/stats/daily', method: 'post', data}),
                providesTags: [{type: 'User' as const, id: 'LIST'}],
            }),
            getUsers: build.query<PageBaseUsersInfo, UsersSearchRequest>({
                query: ({data, params}) => ({url: '/search', method: 'post', data, params}),
                providesTags: (result) => {
                    if (result?.content) {
                        return [
                            {type: 'User' as const, id: 'LIST'},
                            ...result.content.map((user) => ({
                                type: 'User' as const,
                                id: user?.id,
                            })),
                        ];
                    }
                    return [{type: 'User' as const, id: 'LIST'}, 'User' as const];
                },
            }),
            getUserById: build.query<UserDTO, UserByIdRequest>({
                query: (params) => ({url: `/${params.userId}`, method: 'get', params}),
                providesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            setUserAvatar: build.mutation<UserDTO, UserImageRequest>({
                query: ({userId, data}) => ({url: `/${userId}/avatar`, method: 'post', data}),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            updateUser: build.mutation<UserDTO, UserUpdateRequest>({
                query: ({userId, data}) => ({url: `/${userId}`, method: 'put', data}),
                invalidatesTags: (_result, _error, arg) => [
                    {type: 'User' as const, id: arg.userId},
                    {type: 'User' as const, id: 'LIST'},
                ],
            }),
            updateUserName: build.mutation<UserDTO, UserNameUpdateRequest>({
                query: ({userId, data}) => ({url: `/${userId}/name`, method: 'put', data}),
                invalidatesTags: (_result, _error, arg) => [
                    {type: 'User' as const, id: arg.userId},
                    {type: 'User' as const, id: 'LIST'},
                ],
            }),
            deleteUserAccount: build.mutation<UserDTO, UserDeleteRequest>({
                query: ({userId, data}) => ({url: `/${userId}`, method: 'delete', params: data}),
                invalidatesTags: (_result, _error, arg) => [
                    {type: 'User' as const, id: arg.userId},
                    {type: 'User' as const, id: 'LIST'},
                ],
            }),
            createRfid: build.mutation<UserDTO, AddRfidRequest>({
                query: ({userId, data}) => ({url: `/${userId}/ocppTag`, method: 'post', params: data}),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            updateRfid: build.mutation<UserDTO, UpdateRfidRequest>({
                query: ({params, body, path}) => ({
                    url: `/${path.userId}/ocppTag/${path.ocppTagId}`,
                    method: 'put',
                    params,
                    data: body,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.path.userId}],
            }),
            deleteRfid: build.mutation<UserDTO, DeleteRfidRequest>({
                query: ({path, params}) => ({
                    url: `/${path.userId}/ocppTag/${path.ocppTagId}`,
                    method: 'delete',
                    params,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.path.userId}],
            }),
            deleteUser: build.mutation<DefaultResponse, { businessId: number; userId: number }>({
                query: ({businessId, userId}) => ({
                    url: `/${userId}`,
                    method: 'delete',
                    params: {businessId},
                }),
                invalidatesTags: [{type: 'User' as const, id: 'LIST'}],
            }),
            postFeedback: build.mutation<DefaultResponse, PostFeedbackRequest>({
                query: ({userId, data}) => ({url: `/${userId}/feedback`, method: 'post', data}),
            }),
            createManager: build.mutation<UserDTO, CreateUserRequest>({
                query: ({data, businessId}) => ({
                    url: '/managers',
                    method: 'post',
                    data,
                    params: {businessId},
                }),
                invalidatesTags: (_result, _error, _arg) => [{type: 'User' as const, id: 'LIST'}],
            }),
            updateManager: build.mutation<UserDTO, UpdateManagerRequest>({
                query: ({userId, data}) => ({
                    url: `/managers/${userId}`,
                    method: 'put',
                    data,
                }),
                invalidatesTags: (_result, _error, arg) => [
                    {type: 'User' as const, id: arg.userId},
                    {type: 'User' as const, id: 'LIST'},
                ],
            }),
            deleteManager: build.mutation<DefaultResponse, { userId: number; businessId: number }>({
                query: ({userId, businessId}) => ({
                    url: `/managers/${userId}`,
                    method: 'delete',
                    params: {businessId},
                }),
                invalidatesTags: [{type: 'User' as const, id: 'LIST'}],
            }),
            linkToBexioAccount: build.mutation<UserDTO, { userId: number; data: LinkingBexioAccountRequest }>({
                query: ({userId, data}) => ({
                    url: `/${userId}/linkToBexioAccount`,
                    method: 'put',
                    data,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            unlinkFromBexioAccount: build.mutation<UserDTO, { userId: number; data: LinkingBexioAccountRequest }>({
                query: ({userId, data}) => ({
                    url: `/${userId}/unlinkFromBexioAccount`,
                    method: 'put',
                    data,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            setPrimaryBexioAccount: build.mutation<UserDTO, { userId: number; data: SetPrimaryBexioAccountRequest }>({
                query: ({userId, data}) => ({
                    url: `/${userId}/setPrimaryBexioAccount`,
                    method: 'put',
                    data,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
            updateUserBexioIdForAccount: build.mutation<UserDTO, {
                userId: number;
                bexioAccountId: number;
                data: UpdateUserBexioIdRequest
            }>({
                query: ({userId, bexioAccountId, data}) => ({
                    url: `/${userId}/bexioAccounts/${bexioAccountId}`,
                    method: 'put',
                    data,
                }),
                invalidatesTags: (_result, _error, arg) => [{type: 'User' as const, id: arg.userId}],
            }),
        };
    },
});
